import invokeStandaloneLLMCall from 'common/src/modules/shared/copilot/utils/invokeStandaloneLLMCall';
import {z} from 'zod';
import {getCellsThatNeedToBeSanitizedResponseSchema} from '../sanitizeValuesTool.schemas';
import {StartAndEndDatePairInfo} from '../sanitizeValuesTool.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';

async function getCellsThatNeedToBeSanitized({
  startAndEndDatePairs,
  startDateColumnIndex,
  endDateColumnIndex,
  abortSignal,
}: {
  startAndEndDatePairs: StartAndEndDatePairInfo[];
  startDateColumnIndex: number;
  endDateColumnIndex: number;
  abortSignal: AbortSignal;
}): Promise<z.infer<typeof getCellsThatNeedToBeSanitizedResponseSchema>> {
  const instructions = `## Instructions

Your job is to analyze the rows given below detect cells that have issues.

Your response will contain:
- a list of cells whose value needs to be overriden.
- a list of unresolved cells, which requires user intervention.

### Issues that need to be detected

1. **Ambiguous dates**
   1.1. Find start and end dates that are ambiguous. Here are examples of ambiguous dates:
        - "2.3": could be interpreted as February 3 or March 2, and the year is not provided, so it's ambiguous.
        - "2.3.2025": could be interpreted as February 3, 2025 or March 2, 2025, so it is ambiguous, even though the year is provided.
        - "4.25": Must be April 18, but the year is not provided, so it's ambiguous.
   1.2. Add any ambiguous dates to the list of unresolved cells.
   1.3. If both the start date and end date in a row are ambiguous, add both of them to the list of unresolved cells.

2. **ASAP values**
   2.1. Add any start dates whose value is "ASAP" or anything semantically similar to the list of cells whose value needs to be overridden, with the fixed value being today's date (${
     new Date().toISOString().split('T')[0]
   }).

3. **TBD values**
   3.1. Find rows where **only one** of the start date or end date in the row is empty or "TBD" or anything semantically similar. If found, add the empty (or semantically similar) cell to the list of unresolved cells.
   3.2. If **both** start date and end date in the row are empty or "TBD" or anything semantically similar, this is not an issue, do not add them to the list of unresolved cells.

### Start and end date column indices

- The start date column index: ${startDateColumnIndex}
- The end date column index: ${endDateColumnIndex}

### The rows to analyze:

${JSON.stringify(startAndEndDatePairs)}`;

  const aiResponse = await invokeStandaloneLLMCall({
    messages: [openAISystemMessage(instructions)],
    responseSchema: getCellsThatNeedToBeSanitizedResponseSchema,
    abortSignal,
  });

  return aiResponse;
}

export default getCellsThatNeedToBeSanitized;
